import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { passwordRecoveryPageModuleQuery } from '@data/sanity/queries/page'
import { SanityPasswordRecoveryPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getPageSanityClient } from '@lib/sanity/client'
import {
  defaultModuleQueryParameters,
  getPasswordRecoveryPage,
} from '@lib/sanity/page'
import { Locale } from '@lib/language'
import { CustomStaticPropsContext } from '@lib/routes'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface PasswordRecoveryPageProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  page: SanityPasswordRecoveryPage
  site: SanitySiteFragment
}

const PasswordRecoveryPage = ({
  draftMode,
  draftToken,
  locale,
  page,
  site,
}: PasswordRecoveryPageProps) => {
  if (draftMode && draftToken) {
    return (
      <Layout page={page} site={site}>
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={passwordRecoveryPageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
            }}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  return (
    <Layout page={page} site={site}>
      <Modules modules={page.modules} />
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext
): Promise<GetStaticPropsResult<PasswordRecoveryPageProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode
  )
  const passwordRecoveryPage = await getPasswordRecoveryPage(
    sanityClient,
    context.locale
  )

  if (!passwordRecoveryPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      page: passwordRecoveryPage.page,
      site: passwordRecoveryPage.site,
    },
    revalidate: 60,
  }
}

export default PasswordRecoveryPage
